import { ActionStatus, Room } from '../../../Constants';
import {
    angryDocks, emptyDocks, emptyLodgingCount, ghostedDocks,
    playersWhichMayAcceptThisCard
} from '../../Game/GamePlayerInfo';
import { vanguardPossibilities } from '../../TwoD/Components/Controls/Actions/AdvertisementController';
import {
    available_docks_for_guest,
    availableLaneCards, availableSeekers,
    availableSpecialGuests, availableVanguards, bestDockForCard,
    CARD_FACTIONS,
    CARD_NAMES,
    emptyAvailableLanes,
    get_points_for_card, GUESTS_WHICH_ARE_SAFE_TO_DISCARD,
    highestScoringAvailableCard,
    REGULAR_GUESTS,
    UNUPGRADED_LODGING_IDS
} from '../../TwoD/Common/CardUtilities';
import {
    activateABed,
    activateAnAmenity,
    available_guest_orbits,
    availableStaff,
    canIActivateABedOrAmenity
} from '../../TwoD/Common/StationUtilities';


function place(gamePlayerController, aiPackage, tradelane, dock) {
    if (aiPackage.execute) {
        gamePlayerController.gamePlayerApi.placeGuest(dock, tradelane);
    }
    aiPackage.success = true;
}


function areThereSpecialGuestsToSend(gamePlayerController, aiPackage) {
    let lanes = availableSpecialGuests(gamePlayerController.gameData);
    let question = 'Are there special guests I could send?';
    let result = lanes.length > 0;
    let reasoning = 'Sending Special Guests slows other players.';
    aiPackage.addProvenance(question, result, reasoning);
    return result;
}

export function targetWithSpecialGuest(gamePlayerController, aiPackage) {
    let question = 'Who would not like a Special Guest?';
    let reasoning = 'They cannot currently satisfy this Special Guest.'
    let lanes = availableSpecialGuests(gamePlayerController.gameData);
    let laneCards = lanes.map(laneIndex => gamePlayerController.gameData.orbits[laneIndex]);
    let targetGamePlayer;
    let targetTradelane;
    let targetCard;
    laneCards.forEach((cardId, lanesIndex) => {
        if (targetGamePlayer) return;
        // Single source of truth: only players the shared filter says can
        // legally receive this card. Mirrors the engine's Manual 7.2.5
        // precondition (available_docks_for_guest) so the AI never refers a
        // Guest to a full-docks player who cannot place it.
        let legalTargets = playersWhichMayAcceptThisCard(
            gamePlayerController.gameData, cardId).filter(
            player => player.id !== gamePlayerController.gamePlayerData.id);
        let orderedTargets = [ ...legalTargets ].sort(
            (a, b) => a.current_score - b.current_score);
        for (const candidate of orderedTargets) {
            let docks = available_docks_for_guest(candidate.docks, cardId);
            let satisfied = false;
            docks.forEach(dockIndex => {
                let pointData = get_points_for_card(
                    candidate.station,
                    candidate.robot_assignments,
                    candidate.lodging,
                    cardId, dockIndex,
                    true,
                    false);
                // if satisfied, they could make that choice.
                // hunt for unsatisfied only
                satisfied |= pointData.satisfied;
            })
            if (!satisfied) {
                targetGamePlayer = candidate;
                targetTradelane = lanes[lanesIndex];
                targetCard = laneCards[lanesIndex];
                break;
            }
        }
    })
    if (targetGamePlayer) {
        const message = `Target -> ${targetGamePlayer.name}-${targetGamePlayer.seat} with ${CARD_NAMES[targetCard]}`;
        aiPackage.addToMessage(message);
        let answer = `${targetGamePlayer.name}`;
        let reasoning = 'They will be challenged by a Card.'
        aiPackage.addProvenance(question, answer, reasoning);
        question = 'Which Special Guest?';
        answer = `${CARD_NAMES[targetCard]}`;
        aiPackage.addProvenance(question, answer, reasoning);
        aiPackage.success = true;
    } else {
        aiPackage.addProvenance(question, 'No one.', reasoning);
    }

    if (aiPackage.execute && targetGamePlayer) {
        gamePlayerController.gamePlayerApi.clickSpecialTarget(
            targetGamePlayer.id, targetTradelane
        )
    }

}


function areThereSeekers(gamePlayerController, aiPackage) {
    let lanes = availableSeekers(gamePlayerController.gameData);
    let question = 'Is there a Seeker?';
    let answer = lanes.length > 0;
    let reasoning = 'Seekers can clear ghosts.';
    aiPackage.addProvenance(question, answer, reasoning);
    if (!answer) return answer;

    question = 'Are there angry ghosts?'
    const angry = angryDocks(gamePlayerController.gamePlayerData);
    answer = angry.length > 0;
    reasoning = 'Angry Ghosts cost Snohe!'
    aiPackage.addProvenance(question, answer, reasoning);
    if (answer) return answer;

    question = 'Are any of my docks empty?'
    const empty = emptyDocks(gamePlayerController.gamePlayerData);
    answer = empty.length > 0;
    reasoning = 'I will only risk a Seeker if I have no other choice.'
    aiPackage.addProvenance(question, answer, reasoning);
    if (answer) return false;

    const ghosted = ghostedDocks(gamePlayerController.gamePlayerData);
    question = 'Are any of my docks Ghosted?';
    answer = ghosted.length > 0;
    reasoning = 'Seekers can clear ghosts.';
    aiPackage.addProvenance(question, answer, reasoning);
    return answer;
}

function placeSeeker(gamePlayerController, aiPackage) {
    const lanes = availableSeekers(gamePlayerController.gameData);
    const angry = angryDocks(gamePlayerController.gamePlayerData);
    const lane = lanes.shift()
    let dock;
    if (angry.length > 0) {
        dock = angry.shift();
    } else {
        const ghosted = ghostedDocks(gamePlayerController.gamePlayerData);
        dock = ghosted.shift();
    }

    const cardId = gamePlayerController.gameData.orbits[lane];
    const name = CARD_NAMES[cardId];
    aiPackage.addToMessage(`Place ${name} in Dock ${dock}`);
    if (aiPackage.execute) {
        place(gamePlayerController, aiPackage,
            lane, dock);
    }
    aiPackage.success = true;
}


function areThereVanguards(gamePlayerController, aiPackage) {
    const lanes = availableVanguards(gamePlayerController.gameData);
    let question = 'Is there a Vanguard?';
    let answer = lanes.length > 0;
    let reasoning = 'Vanguards give Lodging.';
    aiPackage.addProvenance(question, answer, reasoning);
    if (!answer) return answer;

    question = 'Am I full of Lodging already?'
    const emptyLodging = emptyLodgingCount(gamePlayerController.gamePlayerData);
    answer = emptyLodging === 0;
    reasoning = 'If I am full a Vanguard is not helpful.'
    aiPackage.addProvenance(question, answer, reasoning);
    if (answer) return false;

    const lane = lanes.pop();
    const cardId = gamePlayerController.gameData.orbits[lane];
    question = 'Is there a Dock?'
    const dockData = bestDockForCard(
        gamePlayerController.gamePlayerData, cardId);
    const dock = dockData.dock;
    answer = false;
    if (dock !== null) {
        const points = dockData.points;
        if (points >= 0) {
            answer = true;
            reasoning = `Dock ${dock} for ${dockData.points} Snohe.`
        } else {
            reasoning = `I cannot satisfy this Vanguard.`
        }
    }
    aiPackage.addProvenance(question, answer, reasoning);
    return answer;
}

function placeVanguard(gamePlayerController, aiPackage) {
    const lanes = availableVanguards(gamePlayerController.gameData);
    const lane = lanes.pop();
    const cardId = gamePlayerController.gameData.orbits[lane];
    const dockData = bestDockForCard(
        gamePlayerController.gamePlayerData, cardId);
    const dock = dockData.dock;

    const name = CARD_NAMES[cardId];
    aiPackage.addToMessage(`Place ${name} in Dock ${dock}`);
    if (aiPackage.execute) {
        place(gamePlayerController, aiPackage,
            lane, dock);
    }
    aiPackage.success = true;
}

function draw(gamePlayerController, aiPackage, tradelane) {
    if (aiPackage.execute) {
        gamePlayerController.gamePlayerApi.draw(tradelane);
    }
    aiPackage.success = true;
}

function isThereRoomToDraw(gamePlayerController, aiPackage) {
    let available = available_guest_orbits(gamePlayerController.gameData.orbits);
    let question = 'Are there spaces to draw into?';
    let result = available.length < 6;
    let reasoning = 'A new Card might be just the solution.';
    aiPackage.addProvenance(question, result, reasoning);
    return result;
}

function drawNewCard(gamePlayerController, aiPackage) {
    let available = emptyAvailableLanes(gamePlayerController.gameData);
    let tradelaneIndex = available[0];
    draw(gamePlayerController, aiPackage, tradelaneIndex);
    aiPackage.addToMessage(`Draw Tradelane ${tradelaneIndex}`);
}


function isThereASafeDiscard(gamePlayerController, aiPackage) {
    let available = availableLaneCards(gamePlayerController.gameData);
    let safe = available.filter(
        cardId => GUESTS_WHICH_ARE_SAFE_TO_DISCARD.includes(cardId));
    let question = 'Are there safe Guests to discard?';
    let result = safe.length > 0;
    let reasoning = 'I need to find a better card.';
    aiPackage.addProvenance(question, result, reasoning);
    return result;
}


function safeDiscard(gamePlayerController, aiPackage) {
    let available = availableLaneCards(gamePlayerController.gameData);
    let safe = available.filter(
        cardId => GUESTS_WHICH_ARE_SAFE_TO_DISCARD.includes(cardId));
    let cardId = safe.pop();
    let index = available.indexOf(cardId);
    const tradelane = available_guest_orbits(gamePlayerController.gameData.orbits)[index];

    aiPackage.addToMessage(`Discard Tradelane ${tradelane} ${CARD_NAMES[cardId]}`);
    if (aiPackage.execute) {
        gamePlayerController.gamePlayerApi.clickDiscard(tradelane);
    }
    aiPackage.success = true;
}



function discard(gamePlayerController, aiPackage, tradelane) {
    let available = availableLaneCards(gamePlayerController.gameData);
    let safe = available.filter(cardId => REGULAR_GUESTS.includes(cardId));
    if (aiPackage.execute) {
        gamePlayerController.draw(tradelane);
    }
    aiPackage.success = true;
}


function _activate(gamePlayerController, aiPackage)  {
    let question = 'Do I have Staff to allocate?';
    let answer = availableStaff(gamePlayerController.gamePlayerData).length > 0;
    let reasoning = 'Activate rooms.';
    aiPackage.addProvenance(question, answer, reasoning);
    if (answer && canIActivateABedOrAmenity(gamePlayerController.gamePlayerData)) {
        aiPackage.addToMessage(`Activate Beds and Amenities`);
        aiPackage.success = true;
        if (aiPackage.execute) {
            if (activateABed(gamePlayerController)) return true;
            if (activateAnAmenity(gamePlayerController)) return true;
        }
    } else {
        return false;
    }
}



function advertise(gamePlayerController, aiPackage) {

    if (_activate(gamePlayerController, aiPackage)) {
        return;
    }

    // activate beds
    // activate amenities

    let question = 'Which Tradelane Guest scores the most?';
    let reasoning = 'All available Guests, all Docks, Max Snohe.';
    let result = `I don't want any of those.`;
    const highScoreData = highestScoringAvailableCard(gamePlayerController);
    if (!aiPackage.success && highScoreData.tradelane !== null) {
        const cardId = gamePlayerController.gameData.orbits[
            highScoreData.tradelane];
        result = CARD_NAMES[cardId];
        aiPackage.addToMessage(`Place ${result} in Dock ${highScoreData.dock}`);
        aiPackage.addProvenance(question, result, reasoning);
        place(gamePlayerController, aiPackage,
            highScoreData.tradelane, highScoreData.dock)
    }
    aiPackage.addProvenance(question, result, reasoning);

    if (!aiPackage.success && areThereVanguards(gamePlayerController, aiPackage)) {
        placeVanguard(gamePlayerController, aiPackage);
    }

    if (!aiPackage.success && areThereSeekers(gamePlayerController, aiPackage)) {
        placeSeeker(gamePlayerController, aiPackage);
    }

    // am i in first place?
    if (!aiPackage.success && areThereSpecialGuestsToSend(gamePlayerController, aiPackage)) {
        targetWithSpecialGuest(gamePlayerController, aiPackage);
    }

    if (!aiPackage.success && isThereRoomToDraw(gamePlayerController, aiPackage)) {
        drawNewCard(gamePlayerController, aiPackage);
    }

    if (!aiPackage.success && isThereASafeDiscard(gamePlayerController, aiPackage)) {
        safeDiscard(gamePlayerController, aiPackage);
    }

    if (!aiPackage.success) {
        // todo: skip?
        aiPackage.addToMessage(`I am confused about Advertisement?`);
    }

    return aiPackage;
}


function addVanguardLodging(gamePlayerController, aiPackage) {

    let question = 'Which Vanguard Lodging to I accept?';
    let reasoning = 'I like Upgraded Lodging.';

    // todo: store data about vanguard outside of player choice.
    const cardId = gamePlayerController.gamePlayerData.special_card;
    const possibilities = vanguardPossibilities(
        gamePlayerController.gameData, gamePlayerController.gamePlayerData, cardId);
    const lodgingIndex = possibilities.indexOf(true);
    let result;
    const roomId = gamePlayerController.gamePlayerData.lodging[lodgingIndex];
    if (roomId) {
        const name = Room.NAMES[roomId];
        result =  `Upgrade ${name} (${lodgingIndex})`;
    } else {
        const newLodging = UNUPGRADED_LODGING_IDS[CARD_FACTIONS[cardId] - 1];
        const name = Room.NAMES[newLodging];
        result =  `New ${name} (${newLodging})`;
    }
    aiPackage.addProvenance(question, result, reasoning);
    aiPackage.addToMessage(result);
    aiPackage.success = true;

    if (aiPackage.execute) {
        gamePlayerController.gamePlayerApi.clickVanguardLodging(lodgingIndex);
    }
}



export default function artificialAdvertisementAction(gamePlayerController, aiPackage) {
    const notCurrentlyImplemented = {
        message: 'Advertise? Not sure what to do here.',
        provenance: []
    }

    switch (gamePlayerController.gamePlayerData.action_status) {
    case ActionStatus.INIT_ID:
        aiPackage.addToMessage('Advertisement Initializing');
        return aiPackage;
    case ActionStatus.WAIT_ID:
        return advertise(gamePlayerController, aiPackage)
    case ActionStatus.ADD_ID:
        return addVanguardLodging(gamePlayerController, aiPackage);
    case ActionStatus.ATTACKING_ID:
        aiPackage.addToMessage('Waiting for Special Guest to be Placed');
        return aiPackage
    case ActionStatus.DONE_ID:
        aiPackage.addToMessage('Advertisement Done Confirmation');
        return aiPackage;
    default:
        return notCurrentlyImplemented;
    }
}


